import {
  UserLoginResDto,
  UserLoginDto,
  UserRefreshTokenDto,
  UserSetPasswordDto,
  UserRefreshTokenResDto,
  UserChangePasswordDto,
  UserChangeEmailDto,
  UserRequestChangePhoneDto,
  UserChangePhoneDto,
  UserVerifyCodeDto,
  ProfessionalUserProfileResponse,
} from '@docbay/schemas';
import axiosInstance from './axiosInstance';
import axiosTokenInstance from './axiosTokenInstance';
import axiosAuthInstance from './axiosAuthInstance';
import { getLanguageFromLocalStorage } from 'applicaiton/sessionStorage/language';
import { ForgotPasswordDto } from 'common/auth/forgotPassword/types';

export const UsersAPI = {
  /**
   * Logs in a user
   * @param data - User login data
   */
  usersLogin: (data: UserLoginDto) =>
    axiosTokenInstance().post<UserLoginResDto>('/users/login', data),

  /**
   * Refreshes the user's token
   * @param data - Data for refreshing the token
   */
  usersRefreshToken: (data: UserRefreshTokenDto) =>
    axiosTokenInstance().post<UserRefreshTokenResDto>(
      '/users/refresh-token',
      data,
    ),

  /**
   * Sends a forgot password email to the user
   * @param data - Data for sending the forgot password email
   */
  usersForgotPassword: (data: ForgotPasswordDto) => {
    const { captchaToken, ...restData } = data;
    return axiosAuthInstance('').post('/users/forgot-password', restData, {
      headers: {
        recaptcha: captchaToken,
      },
    });
  },

  /**
   * Verifies the code sent to the user
   * @param data - Data for verifying the code
   */
  usersVerifyCode: (data: UserVerifyCodeDto) => {
    const language = getLanguageFromLocalStorage();
    return axiosAuthInstance('').post('/users/verify-code', {
      ...data,
      language,
    });
  },

  /**
   * Creates a new password for the user
   * @param data - Data for creating the new password
   */
  usersCreateNewPassword: (data: UserSetPasswordDto) =>
    axiosTokenInstance().post('/users/password', data),

  /**
   * Changes the user's password
   * @param data - Data for changing the password
   * @param token - Authorization token
   */
  userChangePassword: (data: UserChangePasswordDto, token: string) => {
    return axiosAuthInstance(token).post('/users/change-password', data);
  },

  /**
   * Verifies the old email of the user
   * @param params - Parameters for verifying the old email
   * @param token - Authorization token
   */
  userVerifyOldEmail: (
    params: { email: string; language: string },
    token: string,
  ) =>
    axiosAuthInstance(token).post(
      '/users/email/verification-code/request',
      params,
    ),

  /**
   * Sets a new email for the user
   * @param params - Parameters for setting the new email
   * @param token - Authorization token
   */
  userSetNewEmail: (params: UserChangeEmailDto, token: string) =>
    axiosAuthInstance(token).patch('/users/email', params),

  /**
   * Requests a phone verification code for the user
   * @param data - Data for requesting the verification code
   * @param token - Authorization token
   */
  userPhoneVerificationCode: (data: UserRequestChangePhoneDto, token: string) =>
    axiosAuthInstance(token).post(
      '/users/phone/verification-code/request',
      data,
    ),

  /**
   * Sets a new phone number for the user
   * @param params - Parameters for setting the new phone number
   * @param token - Authorization token
   */
  userSetNewPhone: (params: UserChangePhoneDto, token: string) =>
    axiosAuthInstance(token).patch('/users/phone', params),

  /**
   * Signs out the user
   */
  signOut: () => axiosInstance.post('/users/logout'),

  /**
   * Checks if an email already exists in the system
   * @param email - Email to check for existence
   */
  checkUserEmailExisting: (email: string) =>
    axiosInstance.post<boolean>('/users/email', { email }),

  /**
   * Sets a new language for the user
   * @param languageIso - ISO code for the new language
   */
  setUserLanguage: (languageIso: string) =>
    axiosInstance.post('/users/change-language', { languageIso }),

  /**
   * Gets the professional user profile of the current user
   */
  getUserProfile: () =>
    axiosInstance.get<ProfessionalUserProfileResponse>(
      '/professional-user/profile',
    ),

  /**
   * Complete First Login
   */
  completeFirstLogin: () => axiosInstance.post('/users/complete-first-login'),
};
